<!-- AlloyOtlpSection.vue -->
<template>
  <div class="space-y-6 glass-panel dark:text-bg-gray">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="font-semibold text-gray-800 dark:text-gray-200 capitalize">Ingest (OTLP) <span class="text-gray-400 dark:text-gray-500 normal-case font-normal">(Required)</span></h2>
      <div class="relative group">
        <button type="button" class="bg-transparent hover:bg-transparent" @click="$emit('start-tutorial')" aria-label="Section tutorial">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <span class="absolute top-full text-xs left-0 glass-card mb-1 px-1.5 py-0.5 text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="flex flex-col md:col-span-2">
        <label for="otlp_endpoint" class="mb-1 font-medium text-gray-700 dark:text-gray-50">OTLP Ingest Endpoint <span class="text-red-500 font-bold text-base">*</span></label>
        <input id="otlp_endpoint" v-model="modelValue.otlp_endpoint" type="url"
          placeholder="e.g. https://ingest.mivu.io"
          @focus="$emit('clear-error', 'otlp_endpoint')"
          :class="[
            'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
            errors.otlp_endpoint
              ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
          ]" />
        <FieldError :message="errors.otlp_endpoint" />
      </div>
      <div class="flex flex-col">
        <label for="remote_write_org_id" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Org ID (X-Scope-OrgID) <span class="text-red-500 font-bold text-base">*</span></label>
        <input id="remote_write_org_id" v-model="modelValue.remote_write_org_id"
          placeholder="e.g. dev"
          @focus="$emit('clear-error', 'remote_write_org_id')"
          :class="[
            'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
            errors.remote_write_org_id
              ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
          ]" />
        <FieldError :message="errors.remote_write_org_id" />
      </div>
      <div class="flex flex-col">
        <label for="scrape_interval_seconds" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Scrape Interval (seconds)</label>
        <input id="scrape_interval_seconds" v-model.number="modelValue.scrape_interval_seconds" type="number" min="1"
          class="border border-gray-300 dark:bg-gray-600 dark:text-gray-50 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
    </div>

    <div>
      <h3 class="font-medium text-gray-800 dark:text-gray-200 mb-3">Proxy <span class="text-gray-400 dark:text-gray-500 font-normal">(Optional, leave empty for a direct connection)</span></h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="flex flex-col md:col-span-2">
          <label for="proxy_url" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Proxy URL</label>
          <input id="proxy_url" v-model="modelValue.proxy_url" type="url"
            placeholder="http://proxy.example.com:8080"
            @focus="$emit('clear-error', 'proxy_url')"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.proxy_url
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.proxy_url" />
        </div>
        <div class="flex flex-col">
          <label for="proxy_username" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Proxy Username</label>
          <input id="proxy_username" v-model="modelValue.proxy_username"
            placeholder="Username"
            @focus="$emit('clear-error', 'proxy_username')"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.proxy_username
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.proxy_username" />
        </div>
        <div class="flex flex-col">
          <label for="proxy_password" class="mb-1 font-medium text-gray-700 dark:text-gray-50">Proxy Password</label>
          <input id="proxy_password" v-model="modelValue.proxy_password" type="password" autocomplete="new-password"
            :placeholder="modelValue.proxy_password_set ? '•••••• stored, leave blank to keep current' : 'Password'"
            @focus="$emit('clear-error', 'proxy_password')"
            :class="[
              'border rounded px-3 py-2 focus:outline-none dark:bg-gray-600 dark:text-gray-50',
              errors.proxy_password
                ? 'border-red-500 ring-2 ring-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
            ]" />
          <FieldError :message="errors.proxy_password" />
          <button v-if="modelValue.proxy_password_set" type="button"
            class="self-start mt-1 text-sm text-blue-500 hover:text-blue-600 bg-transparent hover:bg-transparent p-0 underline"
            @click="clearStoredPassword">
            Clear stored password
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import FieldError from '@/components/FieldError.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:modelValue', 'clear-error', 'start-tutorial'])

// Forget the stored proxy password: the save payload sends an explicit empty
// string so the backend wipes it (a blank field alone means "keep current").
function clearStoredPassword() {
  props.modelValue.proxy_password = ''
  props.modelValue.proxy_password_set = false
  props.modelValue.proxy_password_clear = true
  emit('clear-error', 'proxy_password')
  emit('clear-error', 'proxy_username')
  emit('clear-error', 'proxy_url')
}
</script>
